/**
 * ČIŠTĚNÍ ADRESY, NEŽ SE ULOŽÍ DO MĚŘENÍ (`customer_events.metadata`).
 *
 * Měření posílá u každé události celou adresu stránky (`window.location.href`) a referer.
 * Query string ale není naše data — je to všechno, co se do adresy dostalo, včetně toho,
 * co tam nepatří. Reálně se to stalo: nativní GET odeslání poptávkového formuláře
 * (formulář bez `action` před hydratací ostrůvku) odnavigovalo na
 * `zajezd/<slug>?firstName=…&email=…&phone=…` a načtení té adresy zapsalo jméno, e-mail
 * a telefon do `customer_events` — bez souhlasu, v otevřené podobě a s druhou kopií
 * v refereru následující stránky. Nativní odeslání zavírá `NativeSubmitGuard.astro`, ale
 * ta obrana je na KLIENTOVI: stará verze skriptu z cache, jiný vstupní bod nebo prostě
 * cizí požadavek na veřejný `/api/track/event` ji obejdou. Proto se adresa čistí ještě
 * jednou tady a čistí ji OBĚ strany.
 *
 * Filtr je POVOLOVACÍ, ne zakazovací, a to je podstatné rozhodnutí. Zakazovací seznam
 * („zahoď `email`, `phone`, `firstName`…") chrání jen před jmény, na která si někdo
 * vzpomněl; každé nové pole formuláře je nová díra. Povolovací seznam naopak propustí
 * jen parametry, které web sám vyrábí a které pro měření něco znamenají — katalogové
 * filtry (`?destination=…&dateFrom=…` nese drtivá většina zobrazení výpisu a je to
 * nejcennější signál, který o návštěvníkovi máme), řazení, atribuci a reklamní click-id.
 * Cokoli jiného padá, ať se to jmenuje jakkoli.
 *
 * Padají tím i tři věci, které jsou horší než PII a v adrese se objevit MOHOU:
 * `?t=` (HMAC token centra předvoleb), `?bm=` (token příjemce newsletteru) a `?token=`
 * (magický odkaz do portálu/nabídky) — živé přístupové údaje, které by jinak skončily
 * v analytické tabulce. Ze stejného důvodu se zahazuje FRAGMENT: `#bm=` / `#t=` je jejich
 * preferovaný přenos.
 */
import { TRIP_FILTER_PARAM_KEYS } from "./tripFilters";
import { WEB_PARAM_SURFACES } from "./webParams";

/**
 * Parametry, které si smí adresa do měření ponechat.
 *
 * Katalogové filtry se berou z jediného zdroje (`TRIP_FILTER_PARAM_KEYS`), takže nový
 * filtr je v měření vidět automaticky a nikdo na tenhle soubor nemusí myslet. Zbytek je
 * vypsaný ručně — je to bezpečnostní hranice, ne konfigurace, a má být vidět na jednom
 * místě celý.
 *
 * Adresa stránky je od řezu 02 (vize podnety-tymu-2026-10, F5.2) česky: názvy parametrů
 * v adrese jsou WEBOVÉ názvy slovníku (`webParams.ts`), ne vnitřní. Bez nich by
 * `sanitizeTrackedUrl` filtry z adresy zahodil, proto se k vnitřním názvům přidávají webové
 * názvy všech ploch slovníku. Povolovací povaha se nemění: přibývají jen názvy, které web
 * sám vyrábí. Vnitřní názvy zůstávají kvůli starším řádkům měření a zbylým anglickým
 * adresám; dotaz nad `customer_events`, který čte filtry z adresy, musí znát obojí.
 */
const INTERNAL_PARAMS: readonly string[] = [
  // Katalogové filtry — hlavní obsahový signál výpisů `/zajezdy` a `/hotely`.
  ...TRIP_FILTER_PARAM_KEYS,
  // Podoba výpisu (řazení, dlaždice/seznam, stránka).
  "sort",
  "view",
  "page",
  // Kontext stránek s vlastním výběrem (inspirace, blog, kontakt).
  "type",
  "category",
  "topic",
  // Krok průvodce cestou (`/kviz`, `/hledac-inspiraci`) — odpovědi jsou klíče filtrů výš.
  "krok",
  // Návrat z platební brány — dělí zobrazení „po zaplacení" od běžného.
  "paid",
  // Atribuce kampaní. Standardní jmenný prostor, žádná osobní hodnota se do něj neposílá.
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  // Reklamní click-id (shodné s `CLICK_ID_PARAMS` webového měření).
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "sklik",
];

export const TRACKED_URL_PARAM_ALLOWLIST: readonly string[] = [
  ...new Set([
    ...INTERNAL_PARAMS,
    // Webové (české) názvy ploch slovníku — adresa stránky po řezu 02.
    ...WEB_PARAM_SURFACES.flatMap((surface) => Object.values(surface.names)),
  ]),
];

const ALLOWED = new Set(TRACKED_URL_PARAM_ALLOWLIST);

/** Strop jedné hodnoty a celé adresy — řádek měření nesmí být neomezeně velký. */
const MAX_PARAM_VALUE_LENGTH = 256;
const MAX_URL_LENGTH = 2048;

/**
 * Adresa zbavená všeho, co není na povolovacím seznamu (a celého fragmentu).
 *
 * Pořadí ponechaných parametrů se nemění — uložená adresa má jít porovnat s tou, kterou
 * návštěvník opravdu viděl. Nerozparsovatelný vstup se neuhaduje: usekne se u prvního
 * `?` nebo `#`, protože o řetězci, kterému nerozumíme, nesmíme tvrdit, že je bezpečný.
 */
export function sanitizeTrackedUrl(raw: unknown): string | undefined {
  if (typeof raw !== "string" || raw === "") return undefined;
  const capped = raw.length > MAX_URL_LENGTH ? raw.slice(0, MAX_URL_LENGTH) : raw;

  let url: URL;
  try {
    url = new URL(capped);
  } catch {
    return capped.split(/[?#]/)[0] || undefined;
  }

  const kept = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (!ALLOWED.has(key)) continue;
    // `set`, ne `append`: opakovaný parametr by jen nafukoval řádek, měření z něj nic nemá.
    kept.set(
      key,
      value.length > MAX_PARAM_VALUE_LENGTH ? value.slice(0, MAX_PARAM_VALUE_LENGTH) : value,
    );
  }
  url.search = kept.toString();
  url.hash = "";
  return url.toString();
}

/** Klíče `metadata`, které měření zná; cokoli jiného se do databáze nedostane. */
const METADATA_KEYS = [
  "url",
  "referrer",
  "linkUrl",
  "searchQuery",
  "campaignId",
  "duration",
  "section",
] as const;

/** Adresní klíče `metadata` — u všech tří se čistí query string i fragment. */
const METADATA_URL_KEYS = new Set<string>(["url", "referrer", "linkUrl"]);

/** Strop textové hodnoty `metadata` (dotaz našeptávače, id kampaně, sekce zinu). */
const MAX_METADATA_TEXT_LENGTH = 512;

export interface TrackedMetadata {
  url?: string;
  referrer?: string;
  linkUrl?: string;
  searchQuery?: string;
  campaignId?: string;
  duration?: number;
  section?: string;
}

/**
 * `metadata` události osekaná na známé klíče s vyčištěnými adresami.
 *
 * Koncový bod `/api/track/event` je VEŘEJNÝ a jeho tělo nikdo nevaliduje schématem, takže
 * bez tohohle kroku si do analytické tabulky uloží libovolný JSON kdokoli. Referer se
 * čistí stejně přísně jako `url`: je to adresa PŘEDCHOZÍ stránky, tedy druhá kopie téhož
 * úniku (přesně tak vznikl druhý ze dvou nalezených řádků).
 */
export function sanitizeTrackedMetadata(raw: unknown): TrackedMetadata | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const source = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  for (const key of METADATA_KEYS) {
    const value = source[key];
    if (value === undefined || value === null) continue;
    if (METADATA_URL_KEYS.has(key)) {
      const clean = sanitizeTrackedUrl(value);
      if (clean) out[key] = clean;
    } else if (typeof value === "number") {
      if (Number.isFinite(value)) out[key] = value;
    } else if (typeof value === "string") {
      if (value !== "") out[key] = value.slice(0, MAX_METADATA_TEXT_LENGTH);
    }
  }

  return Object.keys(out).length ? (out as TrackedMetadata) : undefined;
}
